/** Pure display helpers for the v2 community page (no Angular, no services). */

const GROUP_WINDOW_MS = 5 * 60 * 1000;

export function voteLabel(count: number): string {
  const n = Number(count) || 0;
  return `${n} ${n === 1 ? 'vote' : 'votes'}`;
}

export interface PollOptionView {
  id: string;
  label: string;
  count: number;
  percent: number;
}

/** Vote counts and whole-number percentages for each poll option. */
export function pollOptions(options: any[], votesPerOption: Record<string, number> | undefined): PollOptionView[] {
  const list = Array.isArray(options) ? options : [];
  const counts = list.map(o => Number(votesPerOption?.[o.id]) || 0);
  const total = counts.reduce((a, b) => a + b, 0);
  return list.map((o, i) => ({
    id: o.id,
    label: o.option,
    count: counts[i],
    percent: total ? Math.round((counts[i] / total) * 100) : 0,
  }));
}

export function pollTotal(options: PollOptionView[]): number {
  return options.reduce((a, o) => a + o.count, 0);
}

export interface TipView {
  amount: string;
  receiver: string;
}

/**
 * Tips are posted from the payment page as plain text messages:
 *   current: "I gave a ₹472.19 tip to Preston Iverson"
 *   older:   "gave a tip of ₹472.19 to Preston Iverson"
 */
const TIP_PATTERNS = [
  /^\s*(?:I\s+)?gave\s+a\s+(\S+)\s+tip\s+to\s+(.+?)\s*$/i,
  /^\s*(?:I\s+)?gave\s+a\s+tip\s+of\s+(\S+)\s+to\s+(.+?)\s*$/i,
];

export function parseTip(text: any): TipView | null {
  if (typeof text !== 'string' || text.length > 200) return null;
  for (const re of TIP_PATTERNS) {
    const m = text.match(re);
    if (m) return { amount: m[1], receiver: m[2] };
  }
  return null;
}

function senderId(msg: any): string | undefined {
  return msg?._sender?.userId ?? msg?.publishAt;
}

/**
 * A message joins the previous one (no repeated avatar and name) when the same
 * person sent it within 5 minutes and no divider, poll or tip sits between them.
 */
export function isGroupedWithPrevious(list: any[], index: number, dateChanged: boolean): boolean {
  if (index <= 0 || dateChanged) return false;
  const cur = list[index];
  const prev = list[index - 1];
  if (!cur || !prev || cur.newMsgsStartPoint) return false;
  if (cur.type === 'poll' || prev.type === 'poll') return false;
  if (parseTip(cur.message) || parseTip(prev.message)) return false;
  const a = senderId(cur);
  if (!a || a !== senderId(prev)) return false;
  const gap = Number(cur.timestamp) - Number(prev.timestamp);
  return Number.isFinite(gap) && gap >= 0 && gap <= GROUP_WINDOW_MS;
}

export function initials(name: any): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0][0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : (parts[0][1] || '');
  return (first + last).toUpperCase();
}

/** Space avatar: the space's group logo when known, else null (caller shows initials). */
export function spaceAvatar(channel: any, groups: any[] | undefined): string | null {
  const groupId = channel?.networkGroupId ?? channel?.network_group_id;
  if (!groupId || !Array.isArray(groups)) return null;
  const g = groups.find(x => String(x?.group_id ?? x?.id) === String(groupId));
  return g?.group_logo || g?.group_base_logo || null;
}

export function memberLabel(count: any): string {
  const n = Number(count) || 0;
  return `${n} ${n === 1 ? 'member' : 'members'}`;
}

/** Same rule as CommunityMaincontentComponent.isOptionSelected. */
export function pollSelected(poll: any, currentUserId: string, optionId: string): boolean {
  return !!poll?.userVote?.[currentUserId]?.includes(optionId);
}

/** "Jo is typing…", "Jo and Sam are typing…", "Jo and 2 others are typing…" */
export function typingLabel(names: string[]): string {
  const list = (names || []).filter(Boolean);
  if (!list.length) return '';
  if (list.length === 1) return `${list[0]} is typing…`;
  if (list.length === 2) return `${list[0]} and ${list[1]} are typing…`;
  return `${list[0]} and ${list.length - 1} others are typing…`;
}

export interface PanelMember {
  userId: string;
  nickname: string | null;
  profileUrl?: string;
  shortName?: string;
  userType?: string;
  createdOn?: any;
}

/** Names and photos of everyone seen in this space (message, thread and reaction authors). */
export function knownPeople(messages: any[]): Map<string, { nickname: string; profileUrl?: string }> {
  const people = new Map<string, { nickname: string; profileUrl?: string }>();
  const add = (p: any) => {
    if (p?.userId && p?.nickname && !people.has(p.userId)) people.set(p.userId, { nickname: p.nickname, profileUrl: p.profileUrl });
  };
  (messages || []).forEach(m => {
    add(m?._sender);
    (m?.threadReplies || []).forEach((r: any) => add(r?._sender));
    Object.values(m?.reactions || {}).forEach((r: any) => (r?.users || []).forEach(add));
  });
  return people;
}

/**
 * Public spaces: PubNub channel members only carry ids (fan_<id>); `known` supplies
 * names (people seen in the space, then getPublicProfiles). Unknown ids stay unnamed.
 */
export function publicMembers(pubnubMembers: any, known: (userId: string) => { nickname: string; profileUrl?: string } | undefined): PanelMember[] {
  const rows = Array.isArray(pubnubMembers?.data) ? pubnubMembers.data : [];
  return rows
    .map((row: any) => String(row?.uuid?.id ?? row?.uuid ?? ''))
    .filter((id: string) => id.startsWith('fan_'))
    .map((userId: string) => {
      const p = known(userId);
      return { userId, nickname: p?.nickname ?? null, profileUrl: p?.profileUrl, shortName: p ? initials(p.nickname) : undefined };
    });
}

export interface PresenceSplit {
  online: PanelMember[];
  /** named members first, then members we could not name */
  offline: PanelMember[];
}

/** online === null means presence is off or unknown: everyone is listed as offline (no split shown). */
export function splitByPresence(members: PanelMember[], online: Set<string> | null): PresenceSplit {
  const list = members || [];
  const isOn = (m: PanelMember) => !!online && online.has(m.userId);
  const rest = list.filter(m => !isOn(m));
  return {
    online: list.filter(isOn),
    offline: [...rest.filter(m => !!m.nickname), ...rest.filter(m => !m.nickname)],
  };
}
